import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, CircularProgress, Paper } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction } from './DataTable';
import { useToast } from './ui/ToastProvider';
import { transactionStore, BusinessKeywordChange } from '../utils/transactionStore';
const CheckerBusinessKeywordTable: React.FC = () => {
  const [requests, setRequests] = useState<BusinessKeywordChange[]>([]);
  const [selected, setSelected] = useState<BusinessKeywordChange | null>(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    showToast
  } = useToast();
  const refresh = () => setRequests(transactionStore.getPendingBusinessKeywordChanges());
  useEffect(() => {
    refresh();
    const unsubscribe = transactionStore.subscribe(refresh);
    return unsubscribe;
  }, []);
  const handleAction = (row: BusinessKeywordChange, action: 'approve' | 'reject') => {
    setSelected(row);
    setActionType(action);
    setActionDialogOpen(true);
  };
  const handleConfirmAction = async () => {
    if (!selected || !actionType) return;
    if (!reason.trim()) return;
    setIsLoading(true);
    try {
      if (actionType === 'approve') transactionStore.approveBusinessKeywordChange(selected.id, reason);else transactionStore.rejectBusinessKeywordChange(selected.id, reason);
      showToast(`Keyword ${selected.action === 'ADD' ? 'addition' : 'deletion'} ${actionType === 'approve' ? 'approved' : 'rejected'} for "${selected.keyword}"`, actionType === 'approve' ? 'success' : 'info');
      setActionDialogOpen(false);
      setReason('');
      setSelected(null);
      setActionType(null);
    } finally {
      setIsLoading(false);
    }
  };
  const rowActions: RowAction<BusinessKeywordChange>[] = [{
    icon: 'lucide:check-circle',
    label: 'Approve',
    onClick: row => handleAction(row, 'approve')
  }, {
    icon: 'lucide:x-circle',
    label: 'Reject',
    onClick: row => handleAction(row, 'reject')
  }];
  const columns: Column<BusinessKeywordChange>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-513" data-mp="3vq583r1vsjnm">{row.id}</Typography>
  }, {
    id: 'action',
    label: 'Action',
    renderCell: row => <Chip label={row.action} size="small" sx={{
      fontWeight: 700,
      bgcolor: row.action === 'ADD' ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
      color: row.action === 'ADD' ? '#059669' : '#EF4444'
    }} data-id="element-514" data-mp="3qslv0ugx6cx9" />
  }, {
    id: 'keyword',
    label: 'Keyword',
    renderCell: row => <Typography variant="body2" fontWeight={700} sx={{
      letterSpacing: 0.5
    }} data-id="element-515" data-mp="ibqzy16d5wtq">{row.keyword}</Typography>
  }, {
    id: 'makerReason',
    label: 'Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      maxWidth: 260
    }} data-id="element-516" data-mp="lswle6b0kygm">{row.resubmitReason || row.makerReason}</Typography>
  }, {
    id: 'requestedBy',
    label: 'Requested By',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-517" data-mp="utl1d6jyx8w5">{row.requestedBy}</Typography>
  }, {
    id: 'requestedAt',
    label: 'Requested At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-518" data-mp="ztz9eolkkiu6">{new Date(row.resubmittedAt || row.requestedAt).toLocaleString()}</Typography>
  }];
  return <>
      <DataTable title="Pending Business Keyword Requests" data={requests} columns={columns} rowActions={rowActions} keyField="id" emptyStateMessage="No pending business keyword requests" initialVisibleColumns={['action', 'keyword', 'makerReason', 'requestedBy', 'requestedAt']} data-id="element-519" data-mp="18rrphuz47uim" />

      <Dialog open={actionDialogOpen} onClose={() => !isLoading && setActionDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-520" data-mp="1ng5twac6jr1a">
        <DialogTitle data-id="element-521" data-mp="2p7x82wz167hu">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} data-id="element-522" data-mp="2p7f4ffa1gqzw">
            <Icon icon={actionType === 'approve' ? 'lucide:check-circle' : 'lucide:x-circle'} width={24} color={actionType === 'approve' ? '#10B981' : '#EF4444'} data-id="element-523" data-mp="2k9nfmu4oxkzx" />
            <Typography variant="h6" fontWeight={600} data-id="element-524" data-mp="2z0cp9s2i6s1o">{actionType === 'approve' ? 'Approve' : 'Reject'} Keyword Request</Typography>
          </Box>
        </DialogTitle>
        <DialogContent data-id="element-525" data-mp="26advjjdd66qo">
          <Box sx={{
          mt: 1
        }} data-id="element-526" data-mp="269umks5oysos">
            {selected && <>
                <Typography variant="body2" color="text.secondary" sx={{
              mb: 2
            }} data-id="element-527" data-mp="21bt5gcrtt3fy">
                  {selected.action === 'ADD' ? 'Add' : 'Delete'} Keyword: <strong data-id="element-528" data-mp="2f7y1h5m68r7m">{selected.keyword}</strong>
                </Typography>
                <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: '#F8FAFC'
            }} data-id="element-529" data-mp="2b7e5w6h8xvnl">
                  <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-530" data-mp="3qnv1ppzgmxk5">Maker's Reason</Typography>
                  <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-531" data-mp="3mpxakhm4q1e4">{selected.makerReason}</Typography>
                </Paper>
                {selected.resubmitReason && <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: 'rgba(245, 158, 11, 0.05)',
              borderColor: '#FCD34D'
            }} data-id="element-532" data-mp="323rlg329nrd">
                    <Typography variant="caption" color="#D97706" fontWeight={600} data-id="element-533" data-mp="3kb0jzyse340y">Previous Rejection Reason</Typography>
                    <Typography variant="body2" sx={{
                mt: 0.5,
                mb: 1.5
              }} data-id="element-534" data-mp="3pa3p1j8dda8x">{selected.checkerComment}</Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} data-id="element-535" data-mp="lrk471s0t6o5">Maker's Resubmit Reason</Typography>
                    <Typography variant="body2" sx={{
                mt: 0.5
              }} data-id="element-536" data-mp="73fkbuh8ep3s">{selected.resubmitReason}</Typography>
                  </Paper>}
              </>}
            <TextField label={`Reason for ${actionType === 'approve' ? 'Approval' : 'Rejection'}`} placeholder={`Enter reason for ${actionType === 'approve' ? 'approving' : 'rejecting'} this request...`} multiline rows={4} fullWidth required value={reason} onChange={e => setReason(e.target.value)} disabled={isLoading} data-id="element-537" data-mp="zrbh0z6swh0k" />
          </Box>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2
      }} data-id="element-538" data-mp="19fgtsyemxedj">
          <Button onClick={() => setActionDialogOpen(false)} disabled={isLoading} data-id="element-539" data-mp="15d3mi5k8p8en">Cancel</Button>
          <Button variant="contained" color={actionType === 'approve' ? 'success' : 'error'} onClick={handleConfirmAction} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-541" data-mp="vh0kr0tavbs8" /> : <Icon icon={actionType === 'approve' ? 'lucide:check' : 'lucide:x'} width={16} data-id="element-542" data-mp="qo7iqkp7uhju" />} data-id="element-540" data-mp="10eiugtf55v1a">
            {actionType === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>;
};
export default CheckerBusinessKeywordTable;